import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '../firebase-config';
import { setAuthToken, meApi } from '../services/api';

type AuthContextValue = {
  user: { username: string; email?: string } | null;
  roles: string[];
  /** True when Firebase custom claim master_admin is true (e.g. set via set-admin-claims.js). */
  isMasterAdmin: boolean;
  loading: boolean;
  signIn: (params: { username: string; password: string }) => Promise<{ user: FirebaseUser }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function getIdTokenForUser(firebaseUser: FirebaseUser): Promise<string> {
  const token = await firebaseUser.getIdToken();
  return token;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<{ username: string; email?: string } | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [isMasterAdmin, setIsMasterAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadSession = useCallback(async () => {
    return new Promise<void>((resolve) => {
      onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
        if (firebaseUser) {
          try {
            const token = await getIdTokenForUser(firebaseUser);
            setAuthToken(token);
            const idTokenResult = await firebaseUser.getIdTokenResult();
            const claims = idTokenResult.claims as Record<string, unknown>;
            const roleClaim = claims?.role;
            const adminClaim = claims?.admin;
            const masterAdminClaim = claims?.master_admin;
            const roleList: string[] = [];
            if (typeof roleClaim === 'string') roleList.push(roleClaim);
            if (adminClaim === true && !roleList.includes('admin')) roleList.push('ADMIN');
            // Backend may grant ADMIN via DB / MASTER_ADMIN_EMAIL while Firebase has no custom claims.
            try {
              const me = await meApi.getContext();
              const dbRole = me.role?.toUpperCase();
              if (dbRole === 'ADMIN' && !roleList.some((r) => r.toUpperCase() === 'ADMIN')) {
                roleList.push('ADMIN');
              }
            } catch {
              /* /api/me optional for role merge */
            }
            setUser({
              username: firebaseUser.email || firebaseUser.uid,
              email: firebaseUser.email || undefined,
            });
            setRoles(roleList.length ? roleList : ['MEMBER']);
            setIsMasterAdmin(masterAdminClaim === true);
          } catch {
            setAuthToken(null);
            setUser(null);
            setRoles([]);
            setIsMasterAdmin(false);
          }
        } else {
          setAuthToken(null);
          setUser(null);
          setRoles([]);
          setIsMasterAdmin(false);
        }
        setLoading(false);
        resolve();
      });
    });
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const signIn = useCallback(
    async ({ username, password }: { username: string; password: string }) => {
      const userCredential = await signInWithEmailAndPassword(auth, username, password);
      await loadSession();
      return { user: userCredential.user };
    },
    [loadSession]
  );

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
    setAuthToken(null);
    setUser(null);
    setRoles([]);
    setIsMasterAdmin(false);
  }, []);

  const refreshSession = useCallback(async () => {
    const firebaseUser = auth.currentUser;
    if (firebaseUser) {
      const token = await getIdTokenForUser(firebaseUser);
      setAuthToken(token);
      const idTokenResult = await firebaseUser.getIdTokenResult(true);
      const claims = idTokenResult.claims as Record<string, unknown>;
      const roleClaim = claims?.role;
      const adminClaim = claims?.admin;
      const masterAdminClaim = claims?.master_admin;
      const roleList: string[] = typeof roleClaim === 'string' ? [roleClaim] : [];
      if (adminClaim === true && !roleList.includes('admin')) roleList.push('ADMIN');
      try {
        const me = await meApi.getContext();
        const dbRole = me.role?.toUpperCase();
        if (dbRole === 'ADMIN' && !roleList.some((r) => r.toUpperCase() === 'ADMIN')) {
          roleList.push('ADMIN');
        }
      } catch {
        /* ignore */
      }
      setRoles(roleList.length ? roleList : ['MEMBER']);
      setIsMasterAdmin(masterAdminClaim === true);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      roles,
      isMasterAdmin,
      loading,
      signIn,
      signOut,
      refreshSession,
    }),
    [user, roles, isMasterAdmin, loading, signIn, signOut, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
